if (!customElements.get('bundle-buy-buttons')) {
  customElements.define(
    'bundle-buy-buttons',
    class BundleBuyButtons extends HTMLElement {
      connectedCallback() {
        this.formId = this.dataset.formId;
        try {
          this.variants = JSON.parse(this.querySelector('[data-bbb-variants]').textContent);
        } catch (e) {
          this.variants = [];
        }

        this.addButton = this.querySelector('[data-bbb-add]');
        this.buyButton = this.querySelector('[data-bbb-buy]');
        this.soldOutButton = this.querySelector('[data-bbb-soldout]');
        this.totalElement = this.querySelector('[data-bbb-total]');
        this.compareElement = this.querySelector('[data-bbb-compare]');
        this.toggle = this.querySelector('[data-bbb-toggle]');
        this.breakdown = this.querySelector('[data-bbb-breakdown]');

        this.bar = this.querySelector('[data-bbb-bar]');
        if (this.bar) {
          document.body.appendChild(this.bar);
          this.barTotal = this.bar.querySelector('[data-bbb-bar-total]');
          this.barCompare = this.bar.querySelector('[data-bbb-bar-compare]');
          this.barAddButton = this.bar.querySelector('[data-bbb-bar-add]');
          this.barBuyButton = this.bar.querySelector('[data-bbb-bar-buy]');
          this.barSoldOutButton = this.bar.querySelector('[data-bbb-bar-soldout]');
          this.barAddButton?.addEventListener('click', () => this.submit(false, this.barAddButton));
          this.barBuyButton?.addEventListener('click', () => this.submit(true, this.barBuyButton));
        }

        this.addButton?.addEventListener('click', () => this.submit(false, this.addButton));
        this.buyButton?.addEventListener('click', () => this.submit(true, this.buyButton));
        this.toggle?.addEventListener('click', () => this.classList.toggle('is-open'));

        this.onMutation = (event) => {
          if (event.type === 'input' && !event.target.matches('[name="quantity"], [data-gym-qty]')) return;
          this.recalc();
        };
        document.addEventListener('change', this.onMutation);
        document.addEventListener('input', this.onMutation);

        this.currentVariant = null;
        this.variantChangeUnsubscriber = theme.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event?.data?.variant;
          if (!variant) return;
          this.currentVariant = variant;
          this.recalc();
        });

        this.setupStickyBar();
        this.recalc();
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.onMutation);
        document.removeEventListener('input', this.onMutation);
        this.variantChangeUnsubscriber?.();
        this.selfObserver?.disconnect();
        this.footerObserver?.disconnect();
        this.bar?.remove();
      }

      setupStickyBar() {
        if (!this.bar) return;

        this.buttonsVisible = true;
        this.footerVisible = false;

        this.selfObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => (this.buttonsVisible = entry.isIntersecting));
            this.updateBar();
          },
          { rootMargin: '0px 0px -20px 0px' }
        );
        this.selfObserver.observe(this);

        const footer = document.querySelector('footer.footer-group') || document.querySelector('footer');
        if (footer) {
          this.footerObserver = new IntersectionObserver((entries) => {
            this.footerVisible = entries[0].isIntersecting;
            this.updateBar();
          });
          this.footerObserver.observe(footer);
        }
      }

      updateBar() {
        if (!this.bar) return;
        const show = !this.buttonsVisible && !this.footerVisible;
        this.bar.classList.toggle('is-visible', show);
      }

      get productForm() {
        return document.getElementById(this.formId);
      }

      get mainVariant() {
        if (this.currentVariant) {
          return this.variants.find((variant) => variant.id === this.currentVariant.id) || this.currentVariant;
        }
        const input = this.productForm?.querySelector('[name="id"]');
        const id = input ? parseInt(input.value, 10) : null;
        return this.variants.find((variant) => variant.id === id) || this.variants.find((variant) => variant.available) || this.variants[0] || null;
      }

      get mainQuantity() {
        const input = this.productForm?.querySelector('[name="quantity"]');
        return input ? Math.max(1, parseInt(input.value, 10) || 1) : 1;
      }

      gymItems() {
        return Array.from(document.querySelectorAll('[data-gym-item]'))
          .map((item) => {
            const include = item.querySelector('[data-gym-include]')?.checked ?? false;
            const select = item.querySelector('[data-gym-variant]');
            const hidden = item.querySelector('[data-gym-variant-hidden]');
            let id = null;
            let price = 0;
            let comparePrice = 0;
            if (select && select.selectedIndex >= 0) {
              const option = select.options[select.selectedIndex];
              id = parseInt(select.value, 10);
              price = parseInt(option?.dataset.price, 10) || 0;
              comparePrice = parseInt(option?.dataset.comparePrice, 10) || 0;
            } else if (hidden) {
              id = parseInt(hidden.value, 10);
              price = parseInt(hidden.dataset.price, 10) || 0;
              comparePrice = parseInt(hidden.dataset.comparePrice, 10) || 0;
            }
            const quantityInput = item.querySelector('[data-gym-qty]');
            const quantity = Math.max(1, parseInt(quantityInput?.value, 10) || 1);
            const title = item.querySelector('.gym-card__title')?.textContent.trim() || '';
            return { include, id, price, comparePrice, quantity, title };
          })
          .filter((data) => data.include && data.id);
      }

      lineItems() {
        const lines = [];
        const variant = this.mainVariant;
        if (variant) {
          lines.push({
            id: variant.id,
            quantity: this.mainQuantity,
            price: variant.price,
            comparePrice: variant.compare_at_price || 0,
            title: this.dataset.productTitle || '',
          });
        }
        this.gymItems().forEach((data) =>
          lines.push({ id: data.id, quantity: data.quantity, price: data.price, comparePrice: data.comparePrice, title: data.title })
        );
        return lines;
      }

      money(cents) {
        const format = theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat;
        return theme.Currency.formatMoney(cents, format);
      }

      recalc() {
        const lines = this.lineItems();
        const total = lines.reduce((sum, line) => sum + line.price * line.quantity, 0);
        const totalCompare = lines.reduce((sum, line) => sum + Math.max(line.comparePrice || 0, line.price) * line.quantity, 0);
        const formatted = this.money(total);
        const hasCompare = totalCompare > total;
        const formattedCompare = hasCompare ? this.money(totalCompare) : '';

        if (this.totalElement) this.totalElement.textContent = formatted;
        if (this.barTotal) this.barTotal.textContent = formatted;

        [this.compareElement, this.barCompare].forEach((element) => {
          if (!element) return;
          element.textContent = formattedCompare;
          element.toggleAttribute('hidden', !hasCompare);
        });

        if (this.breakdown) {
          this.breakdown.innerHTML = lines
            .map((line) => {
              const lineTotal = line.price * line.quantity;
              const lineCompare = Math.max(line.comparePrice || 0, line.price) * line.quantity;
              const compareMarkup =
                lineCompare > lineTotal ? `<s class="bundle-buy-buttons__breakdown-compare">${this.money(lineCompare)}</s>` : '';
              return `<div class="bundle-buy-buttons__breakdown-row"><span>${line.title} × ${line.quantity}</span><span class="bundle-buy-buttons__breakdown-prices">${compareMarkup}<span>${this.money(lineTotal)}</span></span></div>`;
            })
            .join('');
        }

        const variant = this.mainVariant;
        const unavailable = !variant || variant.available === false;
        [this.addButton, this.buyButton, this.barAddButton, this.barBuyButton].forEach((button) =>
          button?.toggleAttribute('hidden', unavailable)
        );
        [this.soldOutButton, this.barSoldOutButton].forEach((button) => button?.toggleAttribute('hidden', !unavailable));
      }

      handleErrorMessage(message = false) {
        this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
        if (!this.errorMessage) return;
        this.errorMessage.toggleAttribute('hidden', !message);
        this.errorMessage.textContent = message || '';
      }

      submit(checkout, button) {
        button = button || (checkout ? this.buyButton : this.addButton);

        const items = this.lineItems().map((line) => ({ id: line.id, quantity: line.quantity }));
        if (!items.length) return;

        this.handleErrorMessage();
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('disabled', '');

        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({ items, sections: sectionsToBundle, sections_url: window.location.pathname });

        fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.handleErrorMessage(parsedState.description);
              document.dispatchEvent(new CustomEvent('ajaxProduct:error', { detail: { errorMessage: parsedState.description } }));
              button.removeAttribute('aria-busy');
              button.removeAttribute('disabled')
              if (this.barAddButton) this.barAddButton.removeAttribute('disabled');
              this.recalc();
              return;
            }

            if (checkout) {
              window.location.href = '/checkout';
              return;
            }

            const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
            cartJson['sections'] = parsedState['sections'];

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-buy-buttons', cart: cartJson });
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));
            button.removeAttribute('disabled')
            if (this.barAddButton) this.barAddButton.removeAttribute('disabled');


            document.querySelector('cart-drawer')?.show(button);
            button.removeAttribute('aria-busy');
            this.recalc();
          })
          .catch((error) => {
            console.log(error);
            button.removeAttribute('aria-busy');
            button.removeAttribute('disabled');
            if (this.barAddButton) this.barAddButton.removeAttribute('disabled');
            this.recalc();
          });
      }
    }
  );
}
